import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Tab, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcBarTabs, MprcContainer } from 'components';
import { ClassroomViewDto, CreateEnrollResults, EnrollStatus, EnrollSubordinateFailed, ITrainingViewDto, RegistrationIntoTrainingChecksResults, TrainingType, TrainingViewDto } from 'generated/CoreApi_v1';
import useDidUpdateEffect from 'hooks/useNoInitialEffect';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { SubordinatesSelectorDrawer } from 'views/foundation/user/drawers/SubordinatesSelectorDrawer';
import { LearnPathPhasesView } from 'views/lms/components/LearnPathPhasesView';
import { ConclusionFormDialog } from 'views/lms/student/EnrollmentView/ECourse/ConclusionFormDialog';
import { RegistrationClassroomsView } from 'views/lms/student/Registration/components/RegistrationClassroomsView';
import { RegistrationGeneralView } from 'views/lms/student/Registration/components/RegistrationGeneralView';
import { RegistrationOpinionView } from 'views/lms/student/Registration/components/RegistrationOpinionView';
import { RegistrationRemarksDrawer } from 'views/lms/student/Registration/RegistrationRemarksDrawer';
import { InternalTrainingRegistrationDlg } from 'views/lms/student/Registration/TrainingRegistrationDialog/InternalTrainingRegistrationDlg';
import { InternalTrainingRegistrationPage } from 'views/lms/student/Registration/TrainingRegistrationDialog/InternalTrainingRegistrationPage';

///////////////////////////////////////////////////////////////////////////////

/*
Modos:
- "ownUser": Próprio usuário
- "enrollManager": Gestor de matrículas (permissão TrainingManageEnrollments ou TrainingManageEnrollmentsTargetAudience)
- "userManager": Gerente principal do usuário
*/
type TrainingRegistrationMode = "ownUser" | "enrollManager" | "userManager";

export interface ITrainingRegistrationProps {
    trainingId: string;
    mode?: TrainingRegistrationMode;
    variant?: "dialog" | "page";
    subtitle?: string;
    classroomId?: string;
    masterEnrollmentId?: string;
    readonly?: boolean;
    immediateRegistration?: boolean;
    enrollSubordinateId?: string;
    onClose: (result?: CreateEnrollResults) => void;
}

interface TrainingRegistrationType {
    loading: boolean;
    saving: boolean;
    creatingEnroll: boolean;
    viewError: any;
    training: TrainingViewDto | null;
    setTraining: Dispatch<SetStateAction<TrainingViewDto | null>>;
    registerSubordinates: boolean;
    setRegisterSubordinates: Dispatch<SetStateAction<boolean>>;
    classrooms: ClassroomViewDto[];
    selectedClassroom: number;
    classroomId?: string;
    checks: RegistrationIntoTrainingChecksResults | null;
    fails: EnrollSubordinateFailed[];
    checksFailed: boolean;
    Content: React.ReactNode;
    ActionButton: React.ReactNode;
    currentTab: number;
}

const TrainingRegistrationContext = React.createContext<TrainingRegistrationType>(undefined!);
export const useTrainingRegistrationContext = () => React.useContext(TrainingRegistrationContext);

export const TrainingRegistrationDialog = (props: ITrainingRegistrationProps) => {

    const { trainingId, mode = "ownUser", variant = "dialog", classroomId, readonly, immediateRegistration, enrollSubordinateId, onClose } = props;

    const api = useCoreApi();
    const session = useSession();
    const navigate = useNavigate();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();

    const [training, setTraining] = useState<TrainingViewDto | null>(null);
    const [classrooms, setClassrooms] = useState<ClassroomViewDto[]>([]);
    const [selectedClassroom, setSelectedClassroom] = useState<number>(-1);
    const [fails, setFails] = useState<EnrollSubordinateFailed[]>([]);
    const [registerSubordinates, setRegisterSubordinates] = useState<boolean>(mode == "userManager");
    const [checks, setChecks] = useState<RegistrationIntoTrainingChecksResults | null>(null);

    const [viewError, setViewError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentTab, setTab] = useState<number>(0);
    const [creatingEnroll, setCreatingEnroll] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();

    const masterEnrollmentId = useRef<string | null>(props.masterEnrollmentId);
    const userId = enrollSubordinateId ?? session.user.userId;

    ///
    const checksFailed = useMemo(() =>
        checks != null &&
        (
            checks.uniqueClassroomUnavailable === true
            || checks.trainingAudiencePassed === false
            || checks.nextEnrollDate != null
            || checks.enrollMaxTries != null
            || checks.masterEnrollmentTrainingChecks?.isExclusiveAndFinalized == true
            || classrooms[selectedClassroom]?.trainingPreReqs != null
            || classrooms[selectedClassroom]?.trainingSeatsPassed === false
            || classrooms.length == 0
        )
        , [checks, classrooms, selectedClassroom]);

    ///
    const initialLoad = () => {
        setViewError(null);

        api.v1.lmsRegistration.registrationIntoTrainingChecks({
            trainingId,
            userId: userId,
        }).then(checksResult => {
            setChecks(checksResult.data);

            masterEnrollmentId.current = checksResult.data.masterEnrollmentTrainingChecks?.masterEnrollmentId ?? null;

            Promise.all([
                api.v1.lmsTraining.getTrainingView({ trainingId, incrementTrainingViewCounter: true }),
                api.v1.lmsTraining.getClassroomsView({
                    trainingId,
                    classroomId: classroomId ?? null,
                    availableOnly: true,
                    ignoreChecks: registerSubordinates || mode == "enrollManager",
                    masterEnrollmentId: masterEnrollmentId.current
                })
            ]).then((results) => {
                setTraining(results[0].data);
                setClassrooms(results[1].data);
                if (results[1].data.length > 0) setSelectedClassroom(0);
            })
                .catch((e: any) => {
                    setViewError(e);
                })
                .finally(() => {
                    setLoading(false);
                });
        })
            .catch((e: any) => {
                setViewError(e);
            })
            .finally(() => {
                setLoading(false);
            });

    };

    ///
    const doOrdinaryEnroll = async (messageToApprover?: string) => {
        setCreatingEnroll(true);
        setViewError(null);

        try {
            const result = await api.v1.lmsRegistration.createEnroll({
                trainingId: trainingId,
                classroomId: selectedClassroom >= 0 ? classrooms[selectedClassroom].classroomId : null,
                masterEnrollmentId: masterEnrollmentId.current ?? null,
                userId: userId,
                messageToApprover: messageToApprover ?? null
            });
            if (result.data) {
                if (result.data.enrollStatus == EnrollStatus.WaitingPreApproval)
                    enqueueSnackbar(t("Registration.Training.EnrollCreateWaitingApprover"), { variant: "success" });
                else if (result.data.reused) {
                    enqueueSnackbar(t(result.data.enrollStatus == EnrollStatus.Approved
                        ? "Registration.Training.EnrollReusedApprovedSuccessfull"
                        : "Registration.Training.EnrollReusedSuccessfull"
                    ), { variant: "success" });
                }
                else
                    enqueueSnackbar(t("Registration.Training.EnrollCreateSuccessfull"), { variant: "success" });
                onClose(result.data);
            }
            else {
                enqueueSnackbar(t("Registration.Training.EnrollCreateFailed"), { variant: "error" });
                if (immediateRegistration) onClose();
            }
        } catch (e) {
            setViewError(e);
        }
        finally {
            setCreatingEnroll(false);
        }
    };

    ///
    const doRegisterSubordinates = async (subordinates: string[]) => {
        setViewError(null);
        setSaving(true);
        try {
            var result = await api.v1.lmsRegistration.enrollSubordinates({
                trainingId: trainingId,
                classroomId: selectedClassroom >= 0 ? classrooms[selectedClassroom].classroomId : null,
                subordinates: subordinates
            });

            if (result.data.success > 0) {
                enqueueSnackbar(t("Registration.Training.SubordinatesRegistrationSuccess", { count: result.data.success }), { variant: "success" });
            }

            if (result.data.fails.length > 0) {
                setFails(result.data.fails);
            }
            else onClose();
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setSaving(false);
        }
    };

    ///
    const doAddInterest = async () => {
        setViewError(null);
        setSaving(true);
        try {
            var result = await api.v1.lmsRegistration.addTrainingInterest({ trainingId: trainingId, });
            if (result.data) {
                enqueueSnackbar(t("Registration.Training.AddInterestSuccess"), { variant: "success" });
                setChecks({ ...checks!, hasInterest: true });
            }
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setSaving(false);
        }
    };

    ///
    const doRemoveInterest = async () => {
        setViewError(null);
        setSaving(true);
        try {
            var result = await api.v1.lmsRegistration.removeTrainingInterest({ trainingId: trainingId, });
            if (result.data) {
                enqueueSnackbar(t("Registration.Training.RemoveInterestSuccess"), { variant: "success" });
                setChecks({ ...checks!, hasInterest: false });
            }
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setSaving(false);
        }
    };

    ///
    const handleSubordinateSelection = () => {
        setDialog(
            <SubordinatesSelectorDrawer
                managerId={session.user.userId}
                multi
                onClose={(selected) => {
                    setDialog(null);
                    if (selected) doRegisterSubordinates(selected.map(item => item.userId));
                }}
                title={t("Registration.Training.SelectSubordinatesText")}
                addSelectedButtonText={t("Registration.Training.AddSubordinatesButtonText")}
                onGetData={({ managerId, searchText, pageNumber, pageSize }, signal) =>
                    api.v1.lmsSelectors.trainingEnrollSubordinates({
                        trainingId: trainingId,
                        managerId,
                        searchText,
                        pageNumber,
                        pageSize
                    }, { signal })
                }
            />
        );
    };

    ///
    const handleRequestRegisterWithMessage = () => {
        setDialog(
            <RegistrationRemarksDrawer
                subtitle={training?.name}
                onClose={
                    (remarks) => {
                        setDialog(null);
                        if (remarks) doOrdinaryEnroll(remarks);
                    }
                }
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    // Ao Iniciar
    useEffect(() => {

        if (immediateRegistration)
            doOrdinaryEnroll(undefined);
        else
            initialLoad();
    }, []);

    // Ao solicitar inscrição de subordinados
    useDidUpdateEffect(() => {
        async function getData() {
            setViewError(null);
            setLoading(true);
            try {
                var results = await api.v1.lmsTraining.getClassroomsView({
                    trainingId,
                    classroomId: classroomId ?? null,
                    availableOnly: true,
                    ignoreChecks: registerSubordinates || mode == "enrollManager",
                    masterEnrollmentId: null
                });
                setClassrooms(results.data);
                if (results.data.length < 2 && currentTab == 1) setTab(0);
            }
            catch (e: any) {
                setClassrooms([]);
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (training!.trainingType != TrainingType.LearningPath)
            getData();
    }, [registerSubordinates]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDERS
    ////////////////////////////////////////////////////////////////////////////

    ///
    const allTabs = [
        <RegistrationGeneralView
            training={training}
            classrooms={classrooms}
            selectedClassroom={selectedClassroom}
            onChangeTab={setTab}
            readonly={readonly}
            showHighlights={variant == "dialog" ? "full" : "resume"}
        />
        , <RegistrationClassroomsView
            training={training}
            readonly={readonly}
            classrooms={classrooms}
            selectedClassroom={selectedClassroom}
            onChangeClassroom={setSelectedClassroom}
        />
        , <LearnPathPhasesView
            trainingId={trainingId}
        />
        , <RegistrationOpinionView
            training={training}
            readonly={readonly}
        />
    ];

    ///
    const RenderFails = useMemo(() => {
        return (
            <Box
                sx={(theme) => ({
                    borderColor: theme.palette.muted.main,
                    borderRadius: 1
                })}
            >
                <Box
                    height={150}
                    overflow="auto"
                    className={AppGlobalClassKeys.thinScrollbar}>
                    {
                        fails.map((f, i) => (
                            <p>{f.name}: <Typography color="error">{f.reason}</Typography></p>
                        ))
                    }
                </Box>
            </Box>
        );
    }, [fails]);

    ///
    const RenderTabBlock = () => {
        return (
            <>
                <MprcContainer margin="dense">
                    <MprcBarTabs
                        variant="fullWidth"
                        value={currentTab}
                        onChange={(v, selected) => setTab(selected)}
                    >
                        <Tab value={0} label={t("Registration.Training.TabGeneralViewText")} />
                        {
                            training && classrooms.length > 1 &&
                            <Tab value={1} label={t("Registration.Training.TabClassroomsText")} />
                        }
                        {
                            training && training.trainingType == TrainingType.LearningPath &&
                            <Tab value={2} label={t("Registration.Training.TabLearnPathText")} />
                        }
                        {
                            training && training.ratingCount > 0 &&
                            <Tab value={3} label={t("Registration.Training.TabOpinionText")} />
                        }
                    </MprcBarTabs>
                </MprcContainer>
                <Box
                    height={variant == "dialog" ? "35vh" : "auto"}
                    overflow="auto"
                    className={AppGlobalClassKeys.thinScrollbar}
                >
                    <MprcContainer margin="dense">
                        {allTabs[currentTab]}
                    </MprcContainer>
                </Box>
            </>
        );
    };

    ///
    const RenderContentBlock = useMemo(() => {
        if (!loading && fails.length > 0)
            return RenderFails;

        if (!loading)
            return RenderTabBlock();

        return null;
    }, [loading, fails, selectedClassroom, currentTab]);

    ///
    const ActionButton = useMemo(() => {
        if (training == null || checks == null || readonly) return null;

        const tvData: ITrainingViewDto = selectedClassroom >= 0
            ? classrooms[selectedClassroom]
            : training!;

        if (!registerSubordinates && checksFailed) {
            return variant == "dialog" && <Button
                color="info"
                variant="contained"
                onClick={() => onClose()}
            >
                {t("Registration.Training.CloseButtonText")}
            </Button>;
        }

        if (fails.length == 0 && classrooms.length > 1 && currentTab == 0)
            return <Button
                color="success"
                variant="contained"
                fullWidth={variant == "page"}
                onClick={() => setTab(1)}
            >
                {t("Registration.Training.SelectClassroomText")}
            </Button>;

        if (fails.length == 0 && registerSubordinates)
            return <Button
                color="primary"
                variant="contained"
                onClick={handleSubordinateSelection}
            >
                {t("Registration.Training.EnrollSubordinatesButtonText")}
            </Button>;

        if (fails.length == 0 && tvData.approvers && tvData.approvers.length > 0)
            return (
                <Stack direction="row" spacing={.2}>
                    <Button
                        color="info"
                        variant="contained"
                        onClick={() => doOrdinaryEnroll(undefined)}
                    >
                        {t("Registration.Training.RequestRegisterButtonText")}
                    </Button>
                    <Button
                        title={t("Registration.Training.WriteToApproverToolTip")}
                        color="info"
                        variant="contained"
                        onClick={handleRequestRegisterWithMessage}
                    >
                        <FontAwesomeIcon icon={faPencil} />
                    </Button>
                </Stack>
            );

        if (fails.length == 0 && checks.canInterest)
            return <Button
                color={checks.hasInterest ? "error" : "success"}
                variant="contained"
                fullWidth={variant == "page"}
                onClick={checks.hasInterest ? doRemoveInterest : doAddInterest}
            >
                {t(checks.hasInterest ? "Registration.Training.RemoveInterestButtonText" : "Registration.Training.InterestButtonText")}
            </Button>;

        if (fails.length == 0) {

            if (!training.fullManaged) {
                return <Button
                    fullWidth={variant == "page"}
                    color="success"
                    variant="contained"
                    onClick={() => {
                        setDialog(
                            <ConclusionFormDialog
                                trainingId={training.trainingId}
                                classroomId={classrooms[selectedClassroom].classroomId}
                                readonly={false}
                                enrollmentId={null}
                                onClose={(result) => {
                                    setDialog(null);
                                    if (result) navigate(LmsPaths.StudentHistory);
                                    else onClose(result);
                                }}
                            />
                        );
                    }}
                >
                    {t("Student.EnrollmentView.ECourse.FillFormButtonText")}
                </Button>;

            }

            return <Button
                fullWidth={variant == "page"}
                color="success"
                variant="contained"
                onClick={() => doOrdinaryEnroll()}
            >
                {t("Registration.Training.EnrollButtonText")}
            </Button>;

        }
        return null;
    }, [currentTab, fails, classrooms, registerSubordinates, selectedClassroom, checks]);

    ///
    return (
        <TrainingRegistrationContext.Provider
            value={{
                training,
                setTraining,
                registerSubordinates,
                setRegisterSubordinates,
                classrooms,
                selectedClassroom,
                classroomId,
                checksFailed,
                checks,
                fails,
                saving,
                creatingEnroll,
                viewError,
                loading,
                ActionButton,
                Content: RenderContentBlock,
                currentTab
            }}>
            {dialog}
            {variant == "dialog"
                ? <InternalTrainingRegistrationDlg {...props} />
                : <InternalTrainingRegistrationPage />
            }
        </TrainingRegistrationContext.Provider>
    );
};
